import React, { ReactNode, useEffect, useState, useContext, ReactElement } from "react";
import { Box, Checkbox, Grid, makeStyles } from "@material-ui/core";
import ConfirmationDialogContext from "../Dialog/ConfirmationDialog/ConfirmationDialogContext";
import UploadContext, { UploadListener } from "../../contexts/UploadContext";
import GalleryContext from "../../contexts/GalleryContext";
import FileReference from "./model/FileReference.model";
import FileView from "./FileView";
import FileHandler from "./model/FileHandler.model";
import { tesaBlue, tesaDarkRedError, tesaWhite } from "../../styles/colors";
import LoadedFile from "./model/LoadedFile.model";
import AttachmentDialog from "./AttachmentDialog";
import GalleryFilter, { GeneralFileType } from "./GalleryFilter";
import GalleryButton from "./button/GalleryButton";
import AttachmentDetailDialog from "../Dialog/AttachmentDialog/AttachmentDetailDialog";
import UploadingFile from "./model/UploadingFile.model";
import GalleryUploadButton from "./button/GalleryUploadButton";
import GalleryFlag from "./model/GalleryFlag.model";
import Attachment from "./model/Attachment.model";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import NamingStrategy from "./model/NamingStrategy";
import GalleryHeader from "./GalleryHeader";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../functions/confidentialCheck.function";
import ConfidentialConfirmationDialog from "../Dialog/ConfidentialConfirmationDialog";
import TestRequestApi from "../../api/services/TestRequestApi";
import useEnv from "../../hooks/env";
import { TestRequestContext } from "../../contexts/TestRequestContext";
import { NavigationContext } from "../../contexts/NavigationContext";

const useStyles = makeStyles({
  checkbox: {
    position: "absolute",
    zIndex: 20,
    "& .MuiIconButton-label": {
      backgroundColor: tesaWhite,
    },
    "& .MuiSvgIcon-root": {
      fill: tesaBlue,
    },
  },
});

export interface GalleryProps {
  altLayout?: boolean;
  disabled?: boolean;
  disableUploads?: boolean;
  filterable?: boolean;
  hideIfEmpty?: boolean;
  galleryId: string;
  fileHandler: FileHandler;
  namingStrategy: NamingStrategy;
  attachments: Attachment[];
  onChange?: (value: Attachment[]) => void;
  shouldRenderIndicator?: (file: LoadedFile) => { title: string; icon: ReactElement } | null;
  infoText?: string;
  uploadButtonLabel?: string;
  testRequestId?: string;
}

function Gallery(props: GalleryProps) {
  const {
    altLayout,
    disabled,
    disableUploads,
    filterable,
    hideIfEmpty,
    fileHandler,
    namingStrategy,
    attachments,
    galleryId,
    onChange,
    shouldRenderIndicator,
    infoText,
    uploadButtonLabel,
    testRequestId,
  } = props;
  const { files, attachmentDialogOpen, setFiles, setAttachmentDialogOpen } = useContext(GalleryContext);
  const { enqueue, queue, register, unregister } = useContext(UploadContext);
  const { showSnackbar, showErrorSnackbar, handleHttpError, handleDialogNegativeCallback } =
    useContext(ErrorSnackbarContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const { testRequestObj, updateTestRequestObj } = useContext(TestRequestContext);
  const { updateDirtyFlag } = useContext(NavigationContext);
  const [uploadedFiles, setUploadedFiles] = useState<FileReference[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [images, setImages] = useState<LoadedFile[]>([]);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileReference[]>([]);
  const [current, setCurrent] = useState<LoadedFile>();
  const [generalFileType, setGeneralFileType] = useState<keyof typeof GeneralFileType>("ALL");
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState<{ loadedFile: LoadedFile } | null>(null);
  const currentConfidentiality = testRequestObj?.description?.projectConfidentiality;

  useEffect(() => {
    fetchHighlyConfidentialRegex()
      .then(setConfidentialRegex)
      .catch((error) => console.error("Error loading confidential regex:", error));
  }, []);

  useEffect(() => {
    const listener: UploadListener = {
      serviceId: galleryId,
      onUpload: (context) => {
        const file = context.loadedFile;
        const updatedAttachments = context.result as Attachment[];

        const finalizeAdd = () => {
          onChange?.(updatedAttachments);
          setUploadedFiles((prevState) => [...prevState, file]);
        };

        const handleConfidentialMatch = (regex: RegExp | null) => {
          finalizeAdd();

          if (!regex) {
            return;
          }

          if (currentConfidentiality === "HIGHLY_CONFIDENTIAL") {
            return;
          }

          const valuesToCheck = [file.name];
          if (file.description?.trim()) {
            valuesToCheck.push(file.description.trim());
          }

          if (containsHighlyConfidentialInfo(valuesToCheck, regex)) {
            setCurrent(undefined);
            setIsDetailDialogOpen(false);
            setPendingFile({ loadedFile: file });
            setShowConfidentialDialog(true);
          }
        };

        if (testRequestId) {
          if (currentConfidentiality === "HIGHLY_CONFIDENTIAL") {
            finalizeAdd();
            return;
          }

          if (confidentialRegex) {
            handleConfidentialMatch(confidentialRegex);
            return;
          }

          fetchHighlyConfidentialRegex()
            .then((regex) => {
              setConfidentialRegex(regex);
              handleConfidentialMatch(regex);
            })
            .catch((error) => {
              console.error("Error loading confidential regex during upload:", error);
              finalizeAdd();
            });
          return;
        }

        finalizeAdd();
      },
    };

    register(listener);
    return () => {
      unregister(listener);
    };
  }, [galleryId, register, unregister, confidentialRegex, testRequestId, onChange, currentConfidentiality]);

  useEffect(() => {
    if (!attachments || attachments.length === 0) {
      return;
    }
    setUploadedFiles(() => {
      return [
        ...attachments.map(
          (attachment) =>
            ({
              id: attachment.uuid,
              flag: attachment.flag,
              status: "LOADING",
            } as FileReference),
        ),
      ];
    });
    fileHandler
      .load(attachments)
      .then((values) => {
        setUploadedFiles((prevState) => {
          values.forEach((value) => {
            const index = prevState.map((file) => file.id).indexOf(value.id);
            if (index >= 0) {
              prevState.splice(index, 1, value);
            }
          });
          return prevState.filter((file) => file.status !== "LOADING");
        });
      })
      .catch((error) => {
        handleHttpError(error);
      });
  }, [attachments]);

  useEffect(() => {
    setSelectedFiles((prevState) =>
      prevState.filter((selectedFile) => uploadedFiles.map((file) => file.id).indexOf(selectedFile.id) >= 0),
    );
    setImages(uploadedFiles.filter((file) => file.status === "LOADED").map((file) => file as LoadedFile));
  }, [uploadedFiles]);

  useEffect(() => {
    if (images.length === 0) {
      setIsDetailDialogOpen(false);
    }
  }, [images]);

  const isVisible = (fileReference: FileReference): boolean => {
    if (generalFileType === "ALL") {
      return true;
    }
    if (fileReference.status === "LOADING" || fileReference.status === "UPLOADING") {
      return true;
    }
    return (generalFileType === "IMAGES") === (fileReference as LoadedFile).isImage;
  };

  useEffect(() => {
    setUploadingFiles(
      queue
        .filter((queued) => queued.serviceId === galleryId)
        .filter((queued) => queued.state !== "ERROR")
        .filter((queued) => queued.state !== "DONE" || uploadedFiles.map((f) => f.id).indexOf(queued.id) < 0)
        .map((queued) => {
          return {
            id: `${queued.id}-uploading`,
            name: queued.name,
            progress: queued.progress,
            status: "UPLOADING",
            flag: queued.flag,
          };
        }),
    );
  }, [queue]);

  useEffect(() => {
    setFiles([...uploadedFiles, ...uploadingFiles]);
  }, [uploadedFiles, uploadingFiles]);

  const fileExists = uploadedFiles.length > 0;
  const selectedVisibleFiles = selectedFiles.filter(isVisible);
  const disabledButtons = selectedVisibleFiles.length === 0;
  const visibleFiles = uploadedFiles.filter(isVisible);
  const allSelected = selectedVisibleFiles.length === visibleFiles.length;

  const onSelectAll = () => {
    if (allSelected) {
      setSelectedFiles((prevState) => prevState.filter((file) => !isVisible(file)));
    } else {
      setSelectedFiles((prevState) => {
        const stateCopy = [...prevState];
        visibleFiles.forEach((fileReference) => {
          const index = prevState.map((file) => file.id).indexOf(fileReference.id);
          if (index < 0) {
            stateCopy.push(fileReference);
          }
        });
        return stateCopy;
      });
    }
  };

  const onDownloadSelected = () => {
    setDownloading(true);
    fileHandler
      .downloadZip(selectedVisibleFiles, generalFileType)
      .catch(handleHttpError)
      .finally(() => setDownloading(false));
  };

  const onDelete = (idsToBeDeleted: string[], updatedAttachments: Attachment[]) => {
    onChange?.(updatedAttachments);
    setUploadedFiles((prevState) => {
      const stateCopy = [...prevState];
      idsToBeDeleted.forEach((id) => {
        const index = stateCopy.map((file) => file.id).indexOf(id);
        if (index >= 0) {
          stateCopy.splice(index, 1);
        }
      });
      return stateCopy;
    });
  };

  const onDeleteSelected = () => {
    showConfirmationDialog({
      text: "You are about to delete the selected file(s).",
      confirmText: "Delete",
      confirmColor: tesaDarkRedError,
    })
      .then(() => {
        const idsToBeDeleted = selectedVisibleFiles.map((file) => file.id);
        setDeleting(true);
        fileHandler
          .delete(idsToBeDeleted)
          .then((updatedAttachments) => {
            onDelete(idsToBeDeleted, updatedAttachments);
          })
          .catch(handleHttpError)
          .finally(() => setDeleting(false));
      })
      .catch(handleDialogNegativeCallback);
  };

  const onUpload = (filesForUpload: File[], description: string, flag?: GalleryFlag) => {
    enqueue({
      serviceId: galleryId,
      files: filesForUpload.map((file) => ({
        file,
        description,
        flag,
      })),
      afterUpload: (loadedFile) => fileHandler.attach(loadedFile, flag),
    });
  };

  const onDescriptionChange = (id: string, newDescription: string) => {
    setUploadedFiles((prevState) => {
      const stateCopy = [...prevState];
      const index = stateCopy.map((file) => file.id).indexOf(id);
      if (index >= 0) {
        stateCopy.splice(index, 1, {
          ...stateCopy[index],
          description: newDescription,
        } as LoadedFile);
      }
      return stateCopy;
    });
  };

  const classes = useStyles();

  const renderCheckbox = (fileReference: FileReference): ReactNode => {
    return (
      <Box className={classes.checkbox}>
        <Checkbox
          color="primary"
          checked={selectedFiles.map((file) => file.id).includes(fileReference.id)}
          onChange={() => {
            setSelectedFiles((prevState) => {
              const stateCopy = [...prevState];
              const index = stateCopy.map((file) => file.id).indexOf(fileReference.id);
              if (index >= 0) {
                stateCopy.splice(index, 1);
              } else {
                stateCopy.push(fileReference);
              }
              return stateCopy;
            });
          }}
        />
      </Box>
    );
  };

  const fileReferences = visibleFiles.concat(uploadingFiles);
  fileReferences.sort((a, b) => {
    if (a.flag && a.flag.order >= 0 && b.flag && b.flag.order >= 0) {
      return a.flag.order - b.flag.order;
    }
    if (a.flag && a.flag.order >= 0) {
      return -1;
    }
    if (b.flag && b.flag.order >= 0) {
      return 1;
    }
    return 0;
  });

  const handleProceedConfidential = () => {
    if (!pendingFile || !testRequestId) {
      console.error("Proceed clicked but no pending file or test request ID");
      return;
    }

    testRequestApi
      .updateConfidentiality(testRequestId, "HIGHLY_CONFIDENTIAL")
      .then((response) => {
        updateTestRequestObj(response.data);
        updateDirtyFlag(false);
        showSnackbar("Test request set to highly confidential", "success");
      })
      .catch((error) => {
        console.error("updateConfidentiality failed:", error);
        handleHttpError(error);
      })
      .finally(() => {
        setShowConfidentialDialog(false);
        setPendingFile(null);
      });
  };

  const handleCancelConfidential = () => {
    if (!pendingFile) {
      console.error("Cancel clicked but no pending file");
      return;
    }

    // Delete the uploaded file
    fileHandler
      .delete([pendingFile.loadedFile.id])
      .then((updatedAttachments) => {
        onChange?.(updatedAttachments);
        setUploadedFiles((prevState) => prevState.filter((file) => file.id !== pendingFile.loadedFile.id));
        showErrorSnackbar("Upload cancelled and file removed.");
      })
      .catch((error) => {
        console.error("File deletion failed:", error);
        handleHttpError(error);
      })
      .finally(() => {
        setShowConfidentialDialog(false);
        setPendingFile(null);
      });
  };

  return (
    <>
      <AttachmentDialog
        open={attachmentDialogOpen}
        onSubmit={onUpload}
        onClose={() => setAttachmentDialogOpen(null)}
        onAlert={showErrorSnackbar}
      />
      <AttachmentDetailDialog
        disabled={disabled}
        current={current}
        images={images}
        open={isDetailDialogOpen}
        onClose={() => setIsDetailDialogOpen(false)}
        onDelete={(deletedFileId, updatedAttachments) => onDelete([deletedFileId], updatedAttachments)}
        onDescriptionChange={onDescriptionChange}
        fileHandler={fileHandler}
        namingStrategy={namingStrategy}
        testRequestId={testRequestId}
      />
      <ConfidentialConfirmationDialog
        objectName="attachment"
        open={showConfidentialDialog}
        onCancel={handleCancelConfidential}
        onProceed={handleProceedConfidential}
      />
      {(!hideIfEmpty || files.length > 0) && (
        <>
          <Box display="flex" flexDirection="row" justifyContent="space-between" paddingBottom={altLayout ? 3 : 1}>
            {!altLayout && <GalleryHeader infoText={infoText} />}
            {fileExists && altLayout && (
              <Box display="flex" flexDirection="row">
                <Box marginRight={3}>
                  <GalleryButton text={allSelected ? "Deselect All" : "Select All"} onClick={onSelectAll} />
                </Box>
                <Box marginRight={3}>
                  <GalleryButton
                    loading={downloading}
                    disabled={disabledButtons}
                    icon="DOWNLOAD"
                    text="Download"
                    onClick={onDownloadSelected}
                  />
                </Box>
                {!disabled && (
                  <Box marginRight={3}>
                    <GalleryButton
                      loading={deleting}
                      disabled={disabledButtons}
                      icon="DELETE"
                      text="Delete"
                      onClick={onDeleteSelected}
                    />
                  </Box>
                )}
              </Box>
            )}
            {fileExists && filterable && (
              <Box alignSelf={!altLayout ? "center" : ""}>
                <GalleryFilter generalFileType={generalFileType} setGeneralFileType={setGeneralFileType} />
              </Box>
            )}
          </Box>
          <Grid container spacing={3}>
            {fileReferences.map((fileReference) => (
              <Grid key={fileReference.id} item xs={2}>
                {fileReference.status !== "UPLOADING" && renderCheckbox(fileReference)}
                <FileView
                  file={fileReference}
                  downloadImage={(id) => fileHandler.downloadThumbnail(id)}
                  onHttpError={handleHttpError}
                  onFileClick={() => {
                    if (pendingFile && pendingFile.loadedFile.id === fileReference.id) {
                      return;
                    }
                    if (fileReference.status === "LOADED") {
                      const loadedFile = fileReference as LoadedFile;
                      setCurrent(loadedFile);
                      setIsDetailDialogOpen(true);
                    }
                  }}
                  shouldRenderIndicator={shouldRenderIndicator || (() => null)}
                />
              </Grid>
            ))}
          </Grid>
          <Box marginTop={3} display="flex" flexDirection="row">
            {!disabled && !disableUploads && (
              <Box marginRight={3}>
                <GalleryUploadButton label={uploadButtonLabel} />
              </Box>
            )}
            {fileExists && !altLayout && (
              <>
                <Box marginRight={3}>
                  <GalleryButton text={allSelected ? "Deselect All" : "Select All"} onClick={onSelectAll} />
                </Box>
                <Box marginRight={3}>
                  <GalleryButton
                    loading={downloading}
                    disabled={disabledButtons}
                    icon="DOWNLOAD"
                    text="Download"
                    onClick={onDownloadSelected}
                  />
                </Box>
                {!disabled && (
                  <Box marginRight={3}>
                    <GalleryButton
                      loading={deleting}
                      disabled={disabledButtons}
                      icon="DELETE"
                      text="Delete"
                      onClick={onDeleteSelected}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
}

export default Gallery;
